import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { SPACING } from 'constants/design-system';

import colors from 'config/colors';
import { useGridViewContext } from 'modules/GridView/contexts/GridViewContext';
import { useOrderItems } from 'modules/GridView/utils/useOrderItems';
import { OrderNotesExpanded } from 'modules/OverviewScene/components';
import {
  AttendedList,
  UnattendedList,
} from 'modules/OverviewScene/NotificationsLog';
import { RouteNames } from 'navigation/linkingConfig';
import { TAB_BARS } from 'store/App/constants';
import { useAppSelector } from 'store/hooks';
import { OrderSelectors } from 'store/Orders';
import { ItemPreparationStatuses } from 'store/Orders/enums';
import { getOrderInternalStatus } from 'store/Orders/utils';
import { useLogItemStatusChangedUiUpdate } from 'util/analytics';
import { useDisclosure, UseDisclosureReturn } from 'util/hooks';
import { useHaveHiddenItems } from 'util/hooks/useHaveHiddenItems';
import { useNavigationContext } from 'util/hooks/useNavigationContext';
import { Modal, withErrorBoundary } from 'components';
import { OrderDetailCardForStateContainer } from 'components/OrderDetailCard/OrderDetailCard';
import { OrderDetailCardMenuControlForStateContainer } from 'components/OrderDetailCard/OrderDetailCardMenuControl';
import { OrderDetailCardStateContainer } from 'components/OrderDetailCard/OrderDetailCardStateContainer';

import { OrderError } from '../OrderError';
import { OrderHeader } from '../OrderHeader';
import { FinancialInfoSection } from './FinancialInfo';
import { OrderItems } from './OrderItems';
import type { Dispatch, SetStateAction } from 'react';
import type { Order, OrderItem } from 'store/Orders/types';

type OrderDetailsContentProps = {
  items: OrderItem[];
  order: Order;
  commentExpanded?: boolean;
  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;
};

const OrderDetailsContent = ({
  order,
  items,
  commentExpanded,
  onStatusChanged,
}: OrderDetailsContentProps) => {
  const orderInternalStatus = useMemo(() => {
    return getOrderInternalStatus(order, items);
  }, [order, items]);

  return (
    <View
      style={[
        styles.content,
        {
          flex: 1,
          flexDirection: 'row',
          borderRadius: 8,
          overflow: 'hidden',
          padding: 8,
          backgroundColor: colors.ui.orderKinds[order.kind],
          gap: 8,
        },
      ]}
    >
      <OrderItems order={order} items={items} />
      <ScrollView
        style={{ flex: 1, maxWidth: 500 }}
        contentContainerStyle={{
          flex: 1,
          gap: 8,
        }}
      >
        {!commentExpanded && (
          <>
            <OrderHeader
              order={order}
              items={items}
              orderInternalStatus={orderInternalStatus}
              style={{ padding: 0, paddingTop: 8 }}
              onStatusChanged={onStatusChanged}
            />
            <FinancialInfoSection financialInfo={order.financial_info} />
            <UnattendedList
              // orderUUID={order.uuid}
              items={items}
              style={{ borderRadius: 4 }}
            />
            <AttendedList
              items={items}
              orderUUID={order.uuid}
              style={{ borderRadius: 4 }}
            />
          </>
        )}
        <View
          style={{
            flexDirection: 'row',
            borderRadius: 4,
            overflow: 'hidden',
            flexGrow: 1,
            flexShrink: 0,
          }}
        >
          <OrderNotesExpanded
            style={{
              flex: 1,
              height: 'auto',
              padding: 12,
              alignItems: 'flex-start',
              backgroundColor: 'white',
            }}
            internalNotes={order.internal_notes ?? []}
            specialInstructions={order.special_instructions}
          />
        </View>
      </ScrollView>
    </View>
  );
};

type OrderDetailsProps = {
  order: Order;
  onPressGoBack: () => void;
  hiddenItemDisclosure: UseDisclosureReturn;
  selectedOrderItemUuid: string | undefined;
  setSelectedOrderItemUuid: Dispatch<SetStateAction<string | undefined>>;
};

const OrderDetailsComponent = (props: OrderDetailsProps) => {
  const {
    order,
    onPressGoBack,
    hiddenItemDisclosure,
    selectedOrderItemUuid,
    setSelectedOrderItemUuid,
  } = props;

  const { height } = useWindowDimensions();

  const { items, allItems, filteredItems } = useOrderItems(
    order,
    hiddenItemDisclosure.isOpen,
  );

  const haveHiddenItems = useHaveHiddenItems(
    allItems ?? [],
    filteredItems ?? [],
  );

  return (
    <View
      style={{
        maxHeight: height,
        flexGrow: 1,
        gap: SPACING.theme[6],
        padding: 24,
        paddingHorizontal: 96,
      }}
    >
      <OrderDetailCardStateContainer orderUuid={order.uuid}>
        {(state) => (
          <>
            <View style={{ paddingHorizontal: SPACING.theme[12] }}>
              <OrderDetailCardMenuControlForStateContainer
                {...state}
                selectedOrderItemUuid={selectedOrderItemUuid}
                setSelectedOrderItemUuid={setSelectedOrderItemUuid}
                order={order}
                hasHiddenItems={haveHiddenItems.haveHiddenItems}
                isHiddenItemShown={hiddenItemDisclosure.isOpen}
                onPressShowHiddenItems={() => {
                  hiddenItemDisclosure.onToggle();
                }}
                onPressClose={onPressGoBack}
              />
            </View>
            <OrderDetailCardForStateContainer
              {...state}
              selectedOrderItemUuid={selectedOrderItemUuid}
              setSelectedOrderItemUuid={setSelectedOrderItemUuid}
              order={order}
              items={items ?? []}
              onPressNotificationUnattendedList={() => {
                onPressGoBack?.();
              }}
              onPressNotificationAttendedList={() => {
                onPressGoBack?.();
              }}
              style={{ flexShrink: 1, minHeight: '50%' }}
            />
          </>
        )}
      </OrderDetailCardStateContainer>
    </View>
  );
};

const OrderDetailsWithErrorBoundary = withErrorBoundary(OrderDetailsComponent, {
  screen: RouteNames.Home.Main.Drawer.Tabs.Overview._,
  key: 'overview-details',
  renderFallback: (props) => <OrderError {...props} style={styles.content} />,
});

export const OrderDetails = () => {
  const {
    selectedOrder,
    setSelectedOrder,
    hiddenItemDisclosure,
    selectedOrderItemUuid,
    setSelectedOrderItemUuid,
  } = useGridViewContext();
  const { activeRouteKey } = useNavigationContext();

  const order = useAppSelector((state) => {
    if (!selectedOrder) {
      return null;
    }

    let orders: Order[] = [];

    switch (activeRouteKey) {
      case TAB_BARS.new.value:
        orders = OrderSelectors.selectNotBeingPreparedItemsAsOrder(state);
        break;
      case TAB_BARS.prepared.value:
        orders = OrderSelectors.selectPreparedItemsAsOrder(state);
        break;
      case TAB_BARS.cooking.value:
        orders = OrderSelectors.selectBeingPreparedItemsAsOrder(state);
        break;
      case TAB_BARS.scheduled.value:
        orders = OrderSelectors.selectScheduledItemsAsOrder(state);
        break;
      case TAB_BARS.newAndCooking.value:
        orders =
          selectedOrder.tab === 'new'
            ? OrderSelectors.selectNotBeingPreparedItemsAsOrder(state)
            : OrderSelectors.selectBeingPreparedItemsAsOrder(state);
        break;
      default:
        return null;
    }

    return orders.find((order) => order.uuid === selectedOrder.uuid);
  });

  const onPressGoBack = useCallback(() => {
    hiddenItemDisclosure.onClose();
    setSelectedOrder(null);
    setSelectedOrderItemUuid(undefined);
  }, [hiddenItemDisclosure, setSelectedOrder]);

  useEffect(() => {
    if (!order) {
      onPressGoBack();
    }
  }, [onPressGoBack, order]);

  if (!order) {
    return null;
  }

  return (
    <Modal
      closeButtonColor="white"
      visible={!!selectedOrder}
      onRequestClose={onPressGoBack}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
      contentContainerStyle={{
        backgroundColor: 'transparent',
        shadowOpacity: 0,
        marginHorizontal: 0,
        maxWidth: 2000,
        width: '100%',
        alignSelf: 'center',
        minHeight: '50%',
        maxHeight: '100%',
      }}
    >
      <OrderDetailsWithErrorBoundary
        order={order}
        onPressGoBack={onPressGoBack}
        hiddenItemDisclosure={hiddenItemDisclosure}
        selectedOrderItemUuid={selectedOrderItemUuid}
        setSelectedOrderItemUuid={setSelectedOrderItemUuid}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  content: { marginBottom: 12 },
});
